/**
 * Feedback bij een v2-/DEF-taak: de Frame.io-link (of andere bron) plus de opmerkingen
 * van de klant. Staat in Postgres naast Airtable (PUT /Api/taak-feedback/:id).
 *
 * Waarom: een vervolgversie bouwt op klantfeedback, maar die stond nergens bij de taak —
 * de editor moest mails en Frame.io zelf bij elkaar zoeken (Joris, 14 sep 2026). De PL
 * krijgt een todo zolang dit leeg is (zie /Api/pl-todos).
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MovieIcon from '@mui/icons-material/Movie';
import toast from 'react-hot-toast';

import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import FeedbackPuntenLijst from './FeedbackPuntenLijst';

const isUrl = (s) => /^https?:\/\/\S+$/i.test(String(s || '').trim());

export default function FeedbackVeld({ taak, onLokaal }) {
  const [link, setLink] = useState(taak?.feedbackLink || '');
  const [tekst, setTekst] = useState(taak?.feedbackTekst || '');
  const [bezig, setBezig] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [videos, setVideos] = useState(null);
  // Aantal comments achter de review-link (V4). Stil weg als Frame.io niet gekoppeld is of
  // de link geen share is — de teller is een extraatje, de link is het werk.
  const [comments, setComments] = useState(null);
  useEffect(() => {
    const url = taak?.feedbackLink || '';
    setComments(null);
    if (!/^https:\/\/(f\.io|next\.frame\.io)\//i.test(url)) return undefined;
    let actief = true;
    authFetch(`${AI_ENGINE_URL}/api/v1/frameio/comments-voor-link?url=${encodeURIComponent(url)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actief && d && typeof d.comments === 'number') setComments(d); })
      .catch(() => {});
    return () => { actief = false; };
  }, [taak?.feedbackLink]);

  useEffect(() => {
    setLink(taak?.feedbackLink || '');
    setTekst(taak?.feedbackTekst || '');
  }, [taak?.id, taak?.feedbackLink, taak?.feedbackTekst]);

  // Punten uit een klantmail (Correspondentie → Feedback) tellen ook als 'feedback aanwezig'.
  // De lijst meldt het echte aantal; tot dan geldt wat Dagstart meegaf.
  const [puntenTelling, setPuntenTelling] = useState(null);
  useEffect(() => { setPuntenTelling(null); }, [taak?.id]);
  const puntenAantal = Number(puntenTelling ? puntenTelling.totaal : (taak?.feedbackPunten || 0));
  const gewijzigd = (link || '') !== (taak?.feedbackLink || '') || (tekst || '') !== (taak?.feedbackTekst || '');

  const bewaar = async (velden) => {
    const nieuw = { feedbackLink: link.trim() || null, feedbackTekst: tekst.trim() || null, ...velden };
    if (nieuw.feedbackLink && !isUrl(nieuw.feedbackLink)) {
      toast.error('De link moet met https:// beginnen');
      return false;
    }
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/taak-feedback/${encodeURIComponent(taak.id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nieuw),
      });
      const data = await leesJson(r, 'Feedback opslaan');
      const vorige = data.vorige || {};
      onLokaal?.(taak.id, { feedbackLink: data.feedbackLink, feedbackTekst: data.feedbackTekst,
        feedbackOntbreekt: !(data.feedbackLink || data.feedbackTekst || puntenAantal > 0) && taak.feedbackVereist });
      biedOngedaanAan('Feedback opgeslagen', async () => {
          const r2 = await authFetch(`${BACKEND_URL}/Api/taak-feedback/${encodeURIComponent(taak.id)}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ feedbackLink: vorige.feedbackLink || null, feedbackTekst: vorige.feedbackTekst || null }),
          });
          await leesJson(r2, 'Feedback terugzetten');
          onLokaal?.(taak.id, { feedbackLink: vorige.feedbackLink || null, feedbackTekst: vorige.feedbackTekst || null,
            feedbackOntbreekt: !(vorige.feedbackLink || vorige.feedbackTekst || puntenAantal > 0) && taak.feedbackVereist });
      });
      return true;
    } catch (e) {
      meldFout(e, 'Feedback opslaan');
      return false;
    } finally {
      setBezig(false);
    }
  };

  // Frame.io-video's van dit project als keuzelijst — de review-link plakken is anders
  // zoekwerk in een andere tab. Pas ophalen als je het menu opent.
  const openSuggesties = async (e) => {
    setAnchor(e.currentTarget);
    if (videos !== null || !taak?.projectNumber) return;
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/frameio/videos/project/${encodeURIComponent(taak.projectNumber)}`);
      const data = await leesJson(r, 'Frame.io-video\'s');
      setVideos((data.videos || []).filter((v) => v.review_url || v.player_url));
    } catch {
      setVideos([]);
    }
  };

  const kies = (v) => {
    setAnchor(null);
    const url = v.review_url || v.player_url;
    setLink(url);
    bewaar({ feedbackLink: url });
  };

  if (!taak) return null;
  const ontbreekt = puntenTelling
    ? Boolean(taak.feedbackVereist && !(taak.feedbackLink || taak.feedbackTekst) && puntenAantal === 0)
    : taak.feedbackOntbreekt;

  return (
    <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, border: '1px solid',
      borderColor: ontbreekt ? '#fcd34d' : 'rgba(45,51,53,0.1)',
      backgroundColor: ontbreekt ? '#fffbeb' : 'transparent' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700 }}>Feedback</Typography>
        {taak.feedbackVereist && (
          <Chip size="small" label={ontbreekt ? 'ontbreekt nog' : 'aanwezig'}
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
              backgroundColor: ontbreekt ? '#fde68a' : '#dcfce7',
              color: ontbreekt ? '#78350f' : '#166534' }} />
        )}
        {taak.feedbackVereist && ontbreekt && (
          <Typography sx={{ fontSize: '0.68rem', color: '#92400e' }}>
            v2/DEF: zet de feedback erin vóór de start
          </Typography>
        )}
      </Box>

      <FeedbackPuntenLijst taakId={taak.id} aantal={Number(taak.feedbackPunten || 0)}
        altijd={Boolean(taak.feedbackVereist)} onAantallen={setPuntenTelling} />

      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField size="small" fullWidth placeholder="https://f.io/… of andere link naar de feedback"
          value={link} disabled={bezig}
          onChange={(e) => setLink(e.target.value)}
          onBlur={() => { if ((link || '') !== (taak.feedbackLink || '')) bewaar({ feedbackLink: link.trim() || null }); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          InputProps={{ sx: { fontSize: '0.78rem' }, startAdornment: <LinkIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 0.5 }} /> }}
        />
        {taak.projectNumber && (
          <Tooltip title="Kies een Frame.io-video van dit project" arrow>
            <IconButton size="small" onClick={openSuggesties} disabled={bezig}><MovieIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {comments && (
          <Tooltip title={`${comments.comments} ${comments.comments === 1 ? 'comment' : 'comments'} in Frame.io${comments.bestanden > 1 ? ` (${comments.bestanden} bestanden)` : ''}`} arrow>
            <Chip size="small" label={`💬 ${comments.comments}`}
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, backgroundColor: comments.comments > 0 ? '#dbeafe' : '#f1f5f9', color: comments.comments > 0 ? '#1e3a8a' : '#64748b' }} />
          </Tooltip>
        )}
        {isUrl(taak.feedbackLink) && (
          <Tooltip title="Open de feedback" arrow>
            <IconButton size="small" component="a" href={taak.feedbackLink} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {videos === null && <MenuItem disabled sx={{ fontSize: '0.8rem' }}>Laden…</MenuItem>}
        {videos !== null && videos.length === 0 && (
          <MenuItem disabled sx={{ fontSize: '0.8rem' }}>Geen Frame.io-video's gevonden voor {taak.projectNumber}</MenuItem>
        )}
        {(videos || []).map((v) => (
          <MenuItem key={v.id} onClick={() => kies(v)} sx={{ fontSize: '0.8rem' }}>
            {v.filename}{v.version_number ? ` · v${v.version_number}` : ''}{v.review_url ? '' : ' (spelerlink)'}
          </MenuItem>
        ))}
      </Menu>

      <TextField size="small" fullWidth multiline minRows={2} maxRows={8} sx={{ mt: 0.75 }}
        placeholder="Opmerkingen van de klant (uit mail, gesprek of Frame.io)…"
        value={tekst} disabled={bezig}
        onChange={(e) => setTekst(e.target.value)}
        InputProps={{ sx: { fontSize: '0.78rem' } }}
      />
      {gewijzigd && (tekst || '') !== (taak.feedbackTekst || '') && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
          <Button size="small" onClick={() => setTekst(taak.feedbackTekst || '')} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button size="small" variant="contained" disableElevation disabled={bezig}
            onClick={() => bewaar({ feedbackTekst: tekst.trim() || null })} sx={{ textTransform: 'none' }}>
            Opslaan
          </Button>
        </Box>
      )}
    </Box>
  );
}
